import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import KeyRequestModal from '../components/KeyRequestModal';
import KeyRequestFormConfig from '../components/KeyRequestFormConfig';
import KeyRequestDashboard from '../components/KeyRequestDashboard';
import ReviewForm from '../components/ReviewForm';

const IndieGameDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [game, setGame] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentMediaIndex, setCurrentMediaIndex] = useState(0);
  const [showKeyRequestModal, setShowKeyRequestModal] = useState(false);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [showRequestDashboard, setShowRequestDashboard] = useState(false);
  const [keyRequestFormActive, setKeyRequestFormActive] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [editingReview, setEditingReview] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [currentReviewPage, setCurrentReviewPage] = useState(1);
  const [totalReviewPages, setTotalReviewPages] = useState(1);
  const reviewsPerPage = 10;
  const [expandedComments, setExpandedComments] = useState({});
  const [commentText, setCommentText] = useState({});

  useEffect(() => {
    fetchGameDetails();
    fetchCurrentUser();
  }, [id]);

  useEffect(() => {
    if (id) {
      fetchReviews();
    }
  }, [id, currentReviewPage]);

  const fetchCurrentUser = async () => {
    try {
      const token = localStorage.getItem('token');
      if (token) {
        const response = await axios.get('/api/auth/me', {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        setCurrentUser(response.data.data);
        console.log('[DEBUG] Current user:', response.data.data);
      }
    } catch (err) {
      console.error('Error fetching user:', err);
    }
  };

  const fetchGameDetails = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/indie-games/${id}`);
      setGame(response.data.data);
      console.log('[DEBUG] Game data:', response.data.data);
      console.log('[DEBUG] Game userId:', response.data.data.userId);
      
      // Check if key request form is active
      try {
        const formResponse = await axios.get(`/api/key-requests/form/${id}`);
        setKeyRequestFormActive(!!formResponse.data.form);
        console.log('[DEBUG] Form active:', !!formResponse.data.form);
      } catch (err) {
        setKeyRequestFormActive(false);
        console.log('[DEBUG] No form found or error:', err.message);
      }
    } catch (err) {
      setError('Failed to load game details');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const ensureProtocol = (url) => {
    if (!url) return '#';
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }
    return `https://${url}`;
  };

  const getMediaUrl = (mediaItem) => {
    if (mediaItem.url) {
      return mediaItem.url;
    }
    if (mediaItem.fileId) {
      return `/api/media/${mediaItem.fileId}`;
    }
    return null;
  };

  const handleReviewSuccess = () => {
    setShowReviewForm(false);
    setEditingReview(null);
    fetchGameDetails();
    fetchReviews(); // Refresh reviews after posting a new one
  };

  const handleEditClick = (review) => {
    setEditingReview(review);
    setShowReviewForm(true);
  };

  const fetchReviews = async () => {
    setReviewsLoading(true);
    try {
      const response = await axios.get(`/api/reviews/target/IndieGame/${id}`);
      const allReviews = response.data || [];
      
      // Calculate pagination
      const totalPages = Math.ceil(allReviews.length / reviewsPerPage);
      setTotalReviewPages(totalPages);
      
      // Get reviews for current page
      const startIndex = (currentReviewPage - 1) * reviewsPerPage;
      const endIndex = startIndex + reviewsPerPage;
      const paginatedReviews = allReviews.slice(startIndex, endIndex);
      
      setReviews(paginatedReviews);
    } catch (err) {
      console.error('Failed to load reviews:', err);
    } finally {
      setReviewsLoading(false);
    }
  };

  const handleDeleteReview = async (reviewId) => {
    if (!window.confirm('Are you sure you want to delete this review?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/api/reviews/${reviewId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Refresh reviews and game details
      fetchReviews();
      fetchGameDetails();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to delete review');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
    return `${Math.floor(diffDays / 365)} years ago`;
  };

  const renderStars = (rating) => {
    return (
      <div style={{ display: 'flex', gap: '2px' }}>
        {[1, 2, 3, 4, 5].map((star) => (
          <span
            key={star}
            style={{
              color: star <= rating ? '#FFD700' : '#ddd',
              fontSize: '18px'
            }}
          >
            ★
          </span>
        ))}
      </div>
    );
  };

  const handleLikeReview = async (reviewId) => {
    if (!currentUser) {
      alert('Please login to like reviews');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(`/api/reviews/${reviewId}/like`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Refresh reviews to get updated like count
      fetchReviews();
    } catch (err) {
      console.error('Failed to like review:', err);
      alert(err.response?.data?.message || 'Failed to like review');
    }
  };

  const handleAddComment = async (reviewId) => {
    if (!currentUser) {
      alert('Please login to comment');
      return;
    }

    const text = commentText[reviewId]?.trim();
    if (!text) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(`/api/reviews/${reviewId}/comment`, {
        text
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Clear comment text and refresh reviews
      setCommentText(prev => ({ ...prev, [reviewId]: '' }));
      fetchReviews();
    } catch (err) {
      console.error('Failed to add comment:', err);
      alert(err.response?.data?.message || 'Failed to add comment');
    }
  };

  const toggleComments = (reviewId) => {
    setExpandedComments(prev => ({
      ...prev,
      [reviewId]: !prev[reviewId]
    }));
  };

  const renderStarRating = (rating) => {
    if (!rating || rating === 0) {
      return (
        <div className="Gamers-score" style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          marginBottom: '24px'
        }}>
          <span style={{
            color: 'var(--text-primary)',
            fontSize: '16px',
            fontWeight: '600'
          }}>
            Gamers Score
          </span>
          <div style={{ display: 'flex', gap: '4px' }}>
            {[1, 2, 3, 4, 5].map((star) => (
              <span key={star} style={{ color: '#666', fontSize: '24px' }}>★</span>
            ))}
          </div>
          <span style={{ color: 'var(--text-secondary)', fontSize: '18px', fontWeight: '600' }}>
            0 of 5
          </span>
        </div>
      );
    }
    
    return (
      <div className="Gamers-score" style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        marginBottom: '24px'
      }}>
        <span style={{
          color: 'var(--text-primary)',
          fontSize: '16px',
          fontWeight: '600'
        }}>
          Gamers Score
        </span>
        <div style={{ display: 'flex', gap: '4px' }}>
          {[1, 2, 3, 4, 5].map((star) => (
            <span
              key={star}
              style={{
                color: star <= Math.round(rating) ? '#FFD700' : '#666',
                fontSize: '24px'
              }}
            >
              ★
            </span>
          ))}
        </div>
        <span style={{ color: 'var(--text-accent)', fontSize: '18px', fontWeight: '600' }}>
          {rating.toFixed(1)} of 5
        </span>
      </div>
    );
  };

  const goToPreviousMedia = () => {
    setCurrentMediaIndex((prev) => 
      prev === 0 ? game.media.length - 1 : prev - 1
    );
  };

  const goToNextMedia = () => {
    setCurrentMediaIndex((prev) => 
      prev === game.media.length - 1 ? 0 : prev + 1
    );
  };

  if (loading) {
    return (
      <div className="container" style={{ padding: '40px 20px', minHeight: '60vh' }}>
        <div style={{ textAlign: 'center', padding: '60px 0' }}>
          <div className="spinner"></div>
          <p style={{ color: 'var(--text-secondary)', marginTop: '20px' }}>Loading game details...</p>
        </div>
      </div>
    );
  }

  if (error || !game) {
    return (
      <div className="container" style={{ padding: '40px 20px', minHeight: '60vh' }}>
        <div style={{ textAlign: 'center', padding: '60px 0' }}>
          <h2 style={{ color: 'var(--text-accent)', marginBottom: '16px' }}>Game Not Found</h2>
          <p style={{ color: 'var(--text-secondary)', marginBottom: '24px' }}>{error}</p>
          <button 
            className="btn btn-primary"
            onClick={() => navigate('/indie-games')}
          >
            ← Back to Indie Games
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container" style={{ padding: '40px 20px', maxWidth: '1400px', margin: '0 auto' }}>
      {/* Back Button */}
      <button 
        onClick={() => navigate('/indie-games')}
        style={{
          background: 'var(--card-bg)',
          border: '1px solid var(--border-color)',
          color: 'var(--text-primary)',
          padding: '10px 20px',
          borderRadius: '8px',
          cursor: 'pointer',
          marginBottom: '24px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          fontSize: '14px',
          fontWeight: '600',
          transition: 'all 0.3s ease'
        }}
        onMouseEnter={(e) => {
          e.target.style.background = 'var(--card-bg-secondary)';
        }}
        onMouseLeave={(e) => {
          e.target.style.background = 'var(--card-bg)';
        }}
      >
        ← Games
      </button>

      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '32px' }}>
        {/* Left Column - Media and Description */}
        <div>
          {/* Media Carousel */}
          {game.media && game.media.length > 0 && (
            <div style={{
              position: 'relative',
              width: '100%',
              height: '500px',
              background: 'var(--card-bg-secondary)',
              borderRadius: '12px',
              overflow: 'hidden',
              marginBottom: '32px',
              border: '1px solid var(--border-color)'
            }}>
              {(() => {
                const currentMedia = game.media[currentMediaIndex];
                const mediaUrl = getMediaUrl(currentMedia);
                const isVideo = currentMedia.fileType && currentMedia.fileType.startsWith('video/');
                
                return (
                  <>
                    {isVideo ? (
                      <video 
                        controls
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'contain'
                        }}
                        key={currentMediaIndex}
                      >
                        <source src={mediaUrl} type={currentMedia.fileType || 'video/mp4'} />
                        Your browser does not support the video tag.
                      </video>
                    ) : (
                      <img
                        src={mediaUrl}
                        alt={`${game.name} ${currentMediaIndex + 1}`}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'contain'
                        }}
                      />
                    )}
                  </>
                );
              })()}

              {/* Navigation arrows */}
              {game.media.length > 1 && (
                <>
                  <button
                    onClick={goToPreviousMedia}
                    style={{
                      position: 'absolute',
                      left: '16px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'rgba(0, 0, 0, 0.7)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '50%',
                      width: '48px',
                      height: '48px',
                      fontSize: '24px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'all 0.3s ease',
                      zIndex: 2
                    }}
                  >
                    ‹
                  </button>
                  <button
                    onClick={goToNextMedia}
                    style={{
                      position: 'absolute',
                      right: '16px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'rgba(0, 0, 0, 0.7)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '50%',
                      width: '48px',
                      height: '48px',
                      fontSize: '24px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'all 0.3s ease',
                      zIndex: 2
                    }}
                  >
                    ›
                  </button>
                </>
              )}
            </div>
          )}

          {/* Thumbnail Gallery */}
          {game.media && game.media.length > 1 && (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(7, 120px)',
              gap: '12px',
              marginBottom: '32px',
              overflowX: 'auto',
              paddingBottom: '8px',
              width: '100%',
              maxWidth: '100%',
              WebkitOverflowScrolling: 'touch'
            }}>
              {game.media.map((media, index) => {
                const mediaUrl = getMediaUrl(media);
                const isVideo = media.fileType && media.fileType.startsWith('video/');
                
                return (
                  <div
                    key={index}
                    onClick={() => setCurrentMediaIndex(index)}
                    style={{
                      width: '120px',
                      height: '80px',
                      borderRadius: '8px',
                      overflow: 'hidden',
                      cursor: 'pointer',
                      border: index === currentMediaIndex 
                        ? '3px solid var(--btn-primary)' 
                        : '1px solid var(--border-color)',
                      flexShrink: 0,
                      position: 'relative',
                      transition: 'all 0.3s ease'
                    }}
                  >
                    {isVideo ? (
                      <>
                        <video
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            pointerEvents: 'none'
                          }}
                          preload="metadata"
                        >
                          <source src={mediaUrl} type={media.fileType || 'video/mp4'} />
                        </video>
                        <div style={{
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                          background: 'rgba(0, 0, 0, 0.7)',
                          borderRadius: '50%',
                          width: '32px',
                          height: '32px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          fontSize: '12px'
                        }}>
                          ▶
                        </div>
                      </>
                    ) : (
                      <img
                        src={mediaUrl}
                        alt={`Thumbnail ${index + 1}`}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover'
                        }}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Description */}
          <div style={{
            background: 'var(--card-bg)',
            padding: '32px',
            borderRadius: '12px',
            border: '1px solid var(--border-color)',
            marginBottom: '32px'
          }}>
            <p style={{
              color: 'var(--text-secondary)',
              fontSize: '16px',
              lineHeight: '1.8',
              whiteSpace: 'pre-wrap',
              marginBottom: '0'
            }}>
              {game.description}
            </p>
          </div>

          {/* System Requirements */}
          <div style={{
            background: 'var(--card-bg)',
            padding: '32px',
            borderRadius: '12px',
            border: '1px solid var(--border-color)'
          }}>
            <h3 style={{
              color: 'var(--text-accent)',
              fontSize: '24px',
              fontWeight: '700',
              marginBottom: '24px'
            }}>
              System Requirements
            </h3>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '32px' }}>
              {/* Minimum */}
              <div>
                <h4 style={{
                  color: 'var(--text-primary)',
                  fontSize: '18px',
                  fontWeight: '600',
                  marginBottom: '16px'
                }}>
                  Minimum
                </h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div>
                    <div style={{ color: 'var(--text-secondary)', fontSize: '13px', marginBottom: '4px' }}>
                      OS Version
                    </div>
                    <div style={{ color: 'var(--text-primary)', fontSize: '15px' }}>
                      {game.systemRequirements?.minimum?.os || 'TBD'}
                    </div>
                  </div>
                  <div>
                    <div style={{ color: 'var(--text-secondary)', fontSize: '13px', marginBottom: '4px' }}>
                      Storage
                    </div>
                    <div style={{ color: 'var(--text-primary)', fontSize: '15px' }}>
                      {game.systemRequirements?.minimum?.storage || 'TBD'}
                    </div>
                  </div>
                  <div>
                    <div style={{ color: 'var(--text-secondary)', fontSize: '13px', marginBottom: '4px' }}>
                      DirectX
                    </div>
                    <div style={{ color: 'var(--text-primary)', fontSize: '15px' }}>
                      {game.systemRequirements?.minimum?.directX || 'TBD'}
                    </div>
                  </div>
                  <div>
                    <div style={{ color: 'var(--text-secondary)', fontSize: '13px', marginBottom: '4px' }}>
                      Memory
                    </div>
                    <div style={{ color: 'var(--text-primary)', fontSize: '15px' }}>
                      {game.systemRequirements?.minimum?.memory || 'TBD'}
                    </div>
                  </div>
                  <div>
                    <div style={{ color: 'var(--text-secondary)', fontSize: '13px', marginBottom: '4px' }}>
                      Processor
                    </div>
                    <div style={{ color: 'var(--text-primary)', fontSize: '15px' }}>
                      {game.systemRequirements?.minimum?.processor || 'TBD'}
                    </div>
                  </div>
                  <div>
                    <div style={{ color: 'var(--text-secondary)', fontSize: '13px', marginBottom: '4px' }}>
                      Graphics
                    </div>
                    <div style={{ color: 'var(--text-primary)', fontSize: '15px' }}>
                      {game.systemRequirements?.minimum?.graphics || 'TBD'}
                    </div>
                  </div>
                </div>
              </div>

              {/* Recommended */}
              <div>
                <h4 style={{
                  color: 'var(--text-primary)',
                  fontSize: '18px',
                  fontWeight: '600',
                  marginBottom: '16px'
                }}>
                  Recommended
                </h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div>
                    <div style={{ color: 'var(--text-secondary)', fontSize: '13px', marginBottom: '4px' }}>
                      OS Version
                    </div>
                    <div style={{ color: 'var(--text-primary)', fontSize: '15px' }}>
                      {game.systemRequirements?.recommended?.os || 'TBD'}
                    </div>
                  </div>
                  <div>
                    <div style={{ color: 'var(--text-secondary)', fontSize: '13px', marginBottom: '4px' }}>
                      Storage
                    </div>
                    <div style={{ color: 'var(--text-primary)', fontSize: '15px' }}>
                      {game.systemRequirements?.recommended?.storage || 'TBD'}
                    </div>
                  </div>
                  <div>
                    <div style={{ color: 'var(--text-secondary)', fontSize: '13px', marginBottom: '4px' }}>
                      DirectX
                    </div>
                    <div style={{ color: 'var(--text-primary)', fontSize: '15px' }}>
                      {game.systemRequirements?.recommended?.directX || 'TBD'}
                    </div>
                  </div>
                  <div>
                    <div style={{ color: 'var(--text-secondary)', fontSize: '13px', marginBottom: '4px' }}>
                      Memory
                    </div>
                    <div style={{ color: 'var(--text-primary)', fontSize: '15px' }}>
                      {game.systemRequirements?.recommended?.memory || 'TBD'}
                    </div>
                  </div>
                  <div>
                    <div style={{ color: 'var(--text-secondary)', fontSize: '13px', marginBottom: '4px' }}>
                      Processor
                    </div>
                    <div style={{ color: 'var(--text-primary)', fontSize: '15px' }}>
                      {game.systemRequirements?.recommended?.processor || 'TBD'}
                    </div>
                  </div>
                  <div>
                    <div style={{ color: 'var(--text-secondary)', fontSize: '13px', marginBottom: '4px' }}>
                      Graphics
                    </div>
                    <div style={{ color: 'var(--text-primary)', fontSize: '15px' }}>
                      {game.systemRequirements?.recommended?.graphics || 'TBD'}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Reviews Section - Below System Requirements */}
          <div style={{
            background: 'var(--card-bg)',
            padding: '32px',
            borderRadius: '12px',
            border: '1px solid var(--border-color)',
            marginTop: '24px'
          }}>
            <h3 style={{
              color: 'var(--text-accent)',
              fontSize: '24px',
              fontWeight: '700',
              marginBottom: '24px'
            }}>
              Reviews
            </h3>
            
            {/* Rating Display */}
            <div style={{ marginBottom: '24px' }}>
              {renderStarRating(game.averageRating)}
              {game.totalReviews > 0 && (
                <p style={{
                  color: 'var(--text-secondary)',
                  fontSize: '14px',
                  margin: '8px 0 0 0'
                }}>
                  Based on {game.totalReviews} {game.totalReviews === 1 ? 'review' : 'reviews'}
                </p>
              )}
            </div>

            {/* Write Review Button */}
            <div style={{ marginBottom: '24px' }}>
              {currentUser ? (
                <button
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowReviewForm(!showReviewForm);
                    setEditingReview(null);
                  }}
                  style={{
                    padding: '12px 24px',
                    fontSize: '15px',
                    fontWeight: '600'
                  }}
                >
                  {showReviewForm ? '✖ Cancel' : '✍️ Write Review'}
                </button>
              ) : (
                <p style={{ 
                  fontSize: '14px', 
                  color: 'var(--text-secondary)', 
                  fontStyle: 'italic',
                  margin: 0
                }}>
                  Log in to write a review
                </p>
              )}
            </div>

            {/* Review Form */}
            {showReviewForm && currentUser && (
              <div style={{ marginBottom: '32px' }}>
                <ReviewForm
                  targetType="IndieGame"
                  targetId={game._id}
                  existingReview={editingReview}
                  onSuccess={handleReviewSuccess}
                  onCancel={() => {
                    setShowReviewForm(false);
                    setEditingReview(null);
                  }}
                />
              </div>
            )}

            {/* All Reviews List */}
            <div>
              {reviewsLoading ? (
                <div style={{ 
                  textAlign: 'center', 
                  padding: '40px 0',
                  color: 'var(--text-secondary)'
                }}>
                  <div className="spinner" style={{ marginBottom: '12px' }}></div>
                  Loading reviews...
                </div>
              ) : reviews.length === 0 ? (
                <div style={{
                  textAlign: 'center',
                  padding: '40px 20px',
                  background: 'var(--bg-secondary)',
                  borderRadius: '12px',
                  border: '2px dashed var(--border-color)'
                }}>
                  <div style={{ fontSize: '48px', marginBottom: '16px', opacity: 0.5 }}>💬</div>
                  <p style={{ 
                    color: 'var(--text-secondary)',
                    fontSize: '15px',
                    margin: 0
                  }}>
                    No reviews yet. Be the first to review this game!
                  </p>
                </div>
              ) : (
                <>
                  <div style={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    gap: '20px' 
                  }}>
                    {reviews.map((review) => (
                      <div 
                        key={review._id} 
                        style={{
                          background: 'var(--bg-secondary)',
                          borderRadius: '16px',
                          padding: '24px',
                          border: '1px solid var(--border-color)',
                          transition: 'all 0.3s ease',
                          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
                        }}
                      >
                        {/* Review Header */}
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                          marginBottom: '16px',
                          flexWrap: 'wrap',
                          gap: '12px'
                        }}>
                          <Link 
                            to={`/profile/${review.user?.username}`}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '12px',
                              textDecoration: 'none',
                              transition: 'opacity 0.3s ease'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.opacity = '0.8'}
                            onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                          >
                            {/* User Avatar */}
                            <div style={{
                              width: '48px',
                              height: '48px',
                              borderRadius: '50%',
                              background: 'linear-gradient(135deg, var(--btn-primary), var(--btn-primary-hover))',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: 'white',
                              fontWeight: '600',
                              fontSize: '18px',
                              overflow: 'hidden',
                              boxShadow: '0 2px 8px rgba(102, 126, 234, 0.3)',
                              cursor: 'pointer'
                            }}>
                              {review.user?.profile?.avatar?.url ? (
                                <img
                                  src={review.user.profile.avatar.url}
                                  alt={review.user.username}
                                  style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover'
                                  }}
                                />
                              ) : (
                                review.user?.username?.[0]?.toUpperCase() || '?'
                              )}
                            </div>
                            
                            {/* User Info */}
                            <div>
                              <div style={{
                                color: 'var(--text-primary)',
                                fontWeight: '700',
                                fontSize: '16px'
                              }}>
                                {review.user?.username || 'Anonymous'}
                              </div>
                              <div style={{
                                color: 'var(--text-secondary)',
                                fontSize: '13px',
                                marginTop: '2px'
                              }}>
                                {formatDate(review.createdAt)}
                              </div>
                            </div>
                          </Link>

                          {/* Rating */}
                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                            {renderStars(review.rating)}
                            <span style={{
                              color: 'var(--text-accent)',
                              fontSize: '14px',
                              marginTop: '4px',
                              fontWeight: '600'
                            }}>
                              {review.rating}/5
                            </span>
                          </div>
                        </div>

                        {/* Review Comment */}
                        <p style={{
                          color: 'var(--text-primary)',
                          fontSize: '15px',
                          lineHeight: '1.7',
                          margin: '0 0 16px 0'
                        }}>
                          {review.comment}
                        </p>

                        {/* Like and Comment Actions */}
                        <div style={{
                          display: 'flex',
                          gap: '12px',
                          paddingTop: '16px',
                          borderTop: '1px solid var(--border-color)',
                          flexWrap: 'wrap'
                        }}>
                          <button
                            onClick={() => handleLikeReview(review._id)}
                            style={{
                              background: 'transparent',
                              border: '1px solid var(--border-color)',
                              borderRadius: '8px',
                              padding: '8px 16px',
                              color: 'var(--text-primary)',
                              cursor: 'pointer',
                              fontSize: '14px',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px',
                              transition: 'all 0.3s ease',
                              fontWeight: '500'
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.background = 'var(--btn-primary)';
                              e.target.style.color = 'white';
                              e.target.style.borderColor = 'var(--btn-primary)';
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.background = 'transparent';
                              e.target.style.color = 'var(--text-primary)';
                              e.target.style.borderColor = 'var(--border-color)';
                            }}
                          >
                            👍 Like {review.likes?.length > 0 && `(${review.likes.length})`}
                          </button>
                          
                          <button
                            onClick={() => toggleComments(review._id)}
                            style={{
                              background: 'transparent',
                              border: '1px solid var(--border-color)',
                              borderRadius: '8px',
                              padding: '8px 16px',
                              color: 'var(--text-primary)',
                              cursor: 'pointer',
                              fontSize: '14px',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px',
                              transition: 'all 0.3s ease',
                              fontWeight: '500'
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.background = 'var(--btn-secondary)';
                              e.target.style.color = 'white';
                              e.target.style.borderColor = 'var(--btn-secondary)';
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.background = 'transparent';
                              e.target.style.color = 'var(--text-primary)';
                              e.target.style.borderColor = 'var(--border-color)';
                            }}
                          >
                            💬 Comment {review.comments?.length > 0 && `(${review.comments.length})`}
                          </button>

                          {/* Edit/Delete for owner */}
                          {currentUser && review.user?._id === currentUser._id && (
                            <>
                              <button
                                onClick={() => handleEditClick(review)}
                                style={{
                                  background: 'transparent',
                                  border: '1px solid var(--border-color)',
                                  borderRadius: '8px',
                                  padding: '8px 16px',
                                  color: 'var(--text-primary)',
                                  cursor: 'pointer',
                                  fontSize: '14px',
                                  transition: 'all 0.3s ease',
                                  fontWeight: '500'
                                }}
                              >
                                ✏️ Edit
                              </button>
                              <button
                                onClick={() => handleDeleteReview(review._id)}
                                style={{
                                  background: 'transparent',
                                  border: '1px solid #dc3545',
                                  borderRadius: '8px',
                                  padding: '8px 16px',
                                  color: '#dc3545',
                                  cursor: 'pointer',
                                  fontSize: '14px',
                                  transition: 'all 0.3s ease',
                                  fontWeight: '500'
                                }}
                              >
                                🗑️ Delete
                              </button>
                            </>
                          )}
                        </div>

                        {/* Comments Section */}
                        {expandedComments[review._id] && (
                          <div style={{
                            marginTop: '16px',
                            paddingTop: '16px',
                            borderTop: '1px solid var(--border-color)'
                          }}>
                            {/* Existing Comments */}
                            {review.comments && review.comments.length > 0 && (
                              <div style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '12px',
                                marginBottom: '16px'
                              }}>
                                {review.comments.map((comment, idx) => (
                                  <div key={idx} style={{
                                    background: 'var(--bg-card)',
                                    padding: '12px',
                                    borderRadius: '8px',
                                    border: '1px solid var(--border-color)'
                                  }}>
                                    <div style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '8px',
                                      marginBottom: '8px'
                                    }}>
                                      <span style={{
                                        color: 'var(--text-accent)',
                                        fontWeight: '600',
                                        fontSize: '14px'
                                      }}>
                                        {comment.user?.username || 'Anonymous'}
                                      </span>
                                      <span style={{
                                        color: 'var(--text-secondary)',
                                        fontSize: '12px'
                                      }}>
                                        {formatDate(comment.createdAt)}
                                      </span>
                                    </div>
                                    <p style={{
                                      color: 'var(--text-primary)',
                                      fontSize: '14px',
                                      margin: 0,
                                      lineHeight: '1.5'
                                    }}>
                                      {comment.text}
                                    </p>
                                  </div>
                                ))}
                              </div>
                            )}

                            {/* Add Comment Input */}
                            {currentUser && (
                              <div style={{
                                display: 'flex',
                                gap: '8px'
                              }}>
                                <input
                                  type="text"
                                  placeholder="Write a comment..."
                                  value={commentText[review._id] || ''}
                                  onChange={(e) => setCommentText(prev => ({
                                    ...prev,
                                    [review._id]: e.target.value
                                  }))}
                                  onKeyPress={(e) => {
                                    if (e.key === 'Enter') {
                                      handleAddComment(review._id);
                                    }
                                  }}
                                  style={{
                                    flex: 1,
                                    padding: '10px 16px',
                                    borderRadius: '8px',
                                    border: '1px solid var(--border-color)',
                                    background: 'var(--bg-card)',
                                    color: 'var(--text-primary)',
                                    fontSize: '14px',
                                    outline: 'none'
                                  }}
                                />
                                <button
                                  onClick={() => handleAddComment(review._id)}
                                  className="btn btn-primary"
                                  style={{
                                    padding: '10px 20px',
                                    fontSize: '14px'
                                  }}
                                >
                                  Post
                                </button>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Pending Status Badge */}
                        {review.status === 'pending' && review.user?._id === currentUser?._id && (
                          <div style={{
                            marginTop: '16px',
                            padding: '10px 16px',
                            background: 'rgba(255, 193, 7, 0.1)',
                            border: '1px solid rgba(255, 193, 7, 0.3)',
                            borderRadius: '8px',
                            color: '#FFC107',
                            fontSize: '13px',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '8px',
                            fontWeight: '600'
                          }}>
                            ⏳ Pending approval
                          </div>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Pagination */}
                  {totalReviewPages > 1 && (
                    <div style={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      gap: '8px',
                      marginTop: '32px',
                      flexWrap: 'wrap'
                    }}>
                      <button
                        onClick={() => setCurrentReviewPage(prev => Math.max(1, prev - 1))}
                        disabled={currentReviewPage === 1}
                        className="btn btn-secondary"
                        style={{
                          opacity: currentReviewPage === 1 ? 0.5 : 1,
                          cursor: currentReviewPage === 1 ? 'not-allowed' : 'pointer',
                          fontSize: '14px',
                          padding: '10px 20px'
                        }}
                      >
                        ← Previous
                      </button>

                      {/* Page numbers */}
                      {Array.from({ length: Math.min(5, totalReviewPages) }, (_, i) => {
                        let pageNum;
                        if (totalReviewPages <= 5) {
                          pageNum = i + 1;
                        } else if (currentReviewPage <= 3) {
                          pageNum = i + 1;
                        } else if (currentReviewPage >= totalReviewPages - 2) {
                          pageNum = totalReviewPages - 4 + i;
                        } else {
                          pageNum = currentReviewPage - 2 + i;
                        }

                        return (
                          <button
                            key={pageNum}
                            onClick={() => setCurrentReviewPage(pageNum)}
                            className={`btn ${currentReviewPage === pageNum ? 'btn-primary' : 'btn-secondary'}`}
                            style={{
                              fontSize: '14px',
                              padding: '10px 16px',
                              minWidth: '44px',
                              fontWeight: '600'
                            }}
                          >
                            {pageNum}
                          </button>
                        );
                      })}

                      <button
                        onClick={() => setCurrentReviewPage(prev => Math.min(totalReviewPages, prev + 1))}
                        disabled={currentReviewPage === totalReviewPages}
                        className="btn btn-secondary"
                        style={{
                          opacity: currentReviewPage === totalReviewPages ? 0.5 : 1,
                          cursor: currentReviewPage === totalReviewPages ? 'not-allowed' : 'pointer',
                          fontSize: '14px',
                          padding: '10px 20px'
                        }}
                      >
                        Next →
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        {/* Right Column - Game Info */}
        <div>
          <div style={{
            background: 'var(--card-bg)',
            padding: '32px',
            borderRadius: '12px',
            border: '1px solid var(--border-color)',
            position: 'sticky',
            top: '20px'
          }}>
            {/* Title */}
            <h1 style={{
              color: 'var(--text-accent)',
              fontSize: '32px',
              fontWeight: '700',
              marginBottom: '16px',
              lineHeight: '1.2'
            }}>
              {game.name}
            </h1>

            {/* Publisher/Developer */}
            {game.userId && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginBottom: '16px',
                padding: '12px',
                background: 'var(--card-bg-secondary)',
                borderRadius: '8px'
              }}>
                <span style={{ fontSize: '20px' }}>🎮</span>
                <Link 
                  to={`/profile/${game.userId.username}`}
                  style={{
                    color: 'var(--text-accent)',
                    textDecoration: 'none',
                    fontWeight: '600',
                    fontSize: '15px'
                  }}
                >
                  {game.userId.username}
                </Link>
              </div>
            )}

            {/* Version & Release Status */}
            <div style={{ display: 'flex', gap: '12px', marginBottom: '16px', flexWrap: 'wrap' }}>
              {game.releaseStatus && (
                <span style={{
                  padding: '6px 16px',
                  background: 'var(--card-bg-secondary)',
                  color: 'var(--text-primary)',
                  borderRadius: '20px',
                  fontSize: '13px',
                  fontWeight: '600',
                  border: '1px solid var(--border-color)'
                }}>
                  📅 {game.releaseStatus}
                </span>
              )}
              {game.version && (
                <span style={{
                  padding: '6px 16px',
                  background: 'var(--card-bg-secondary)',
                  color: 'var(--text-primary)',
                  borderRadius: '20px',
                  fontSize: '13px',
                  fontWeight: '600',
                  border: '1px solid var(--border-color)'
                }}>
                  Version: {game.version}
                </span>
              )}
            </div>

            {/* Maturity Rating */}
            {game.maturityRating && (
              <div style={{
                padding: '12px',
                background: 'var(--card-bg-secondary)',
                borderRadius: '8px',
                marginBottom: '16px',
                border: '1px solid var(--border-color)'
              }}>
                <div style={{
                  display: 'inline-block',
                  padding: '4px 12px',
                  background: '#dc3545',
                  color: 'white',
                  borderRadius: '4px',
                  fontSize: '13px',
                  fontWeight: '700'
                }}>
                  {game.maturityRating}
                </div>
                <div style={{
                  color: 'var(--text-secondary)',
                  fontSize: '13px',
                  marginTop: '8px'
                }}>
                  Mature Accompanied
                </div>
              </div>
            )}

            {/* Gamers Score */}
            {renderStarRating(game.averageRating)}

            {/* Genres */}
            {game.genre && game.genre.length > 0 && (
              <div style={{ marginBottom: '24px' }}>
                <h4 style={{
                  color: 'var(--text-primary)',
                  fontSize: '16px',
                  fontWeight: '600',
                  marginBottom: '12px'
                }}>
                  Genre(s)
                </h4>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {game.genre.map((g, index) => (
                    <span
                      key={index}
                      style={{
                        padding: '6px 16px',
                        background: 'var(--card-bg-secondary)',
                        color: 'var(--text-primary)',
                        borderRadius: '20px',
                        fontSize: '13px',
                        fontWeight: '500',
                        border: '1px solid var(--border-color)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}
                    >
                      ❌ {g}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Features */}
            {game.features && game.features.length > 0 && (
              <div style={{ marginBottom: '24px' }}>
                <h4 style={{
                  color: 'var(--text-primary)',
                  fontSize: '16px',
                  fontWeight: '600',
                  marginBottom: '12px'
                }}>
                  Features
                </h4>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {game.features.map((feature, index) => (
                    <span
                      key={index}
                      style={{
                        padding: '6px 16px',
                        background: 'var(--card-bg-secondary)',
                        color: 'var(--text-primary)',
                        borderRadius: '20px',
                        fontSize: '13px',
                        fontWeight: '500',
                        border: '1px solid var(--border-color)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}
                    >
                      🎮 {feature}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Price */}
            <div style={{
              background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1), rgba(118, 75, 162, 0.1))',
              padding: '20px',
              borderRadius: '8px',
              border: '1px solid var(--border-color)',
              marginBottom: '24px'
            }}>
              <div style={{
                fontSize: '13px',
                color: 'var(--text-secondary)',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                marginBottom: '8px'
              }}>
                Price
              </div>
              <div style={{
                fontSize: '28px',
                color: 'var(--btn-success)',
                fontWeight: '700'
              }}>
                {game.price}
              </div>
            </div>

            {/* Store Links */}
            <div style={{ marginBottom: '24px' }}>
              <h4 style={{
                color: 'var(--text-primary)',
                fontSize: '16px',
                fontWeight: '600',
                marginBottom: '12px'
              }}>
                Available On
              </h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {game.storeLinks && game.storeLinks.length > 0 ? (
                  game.storeLinks.map((link, index) => (
                    <a
                      key={index}
                      href={ensureProtocol(link.url)}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        padding: '14px 24px',
                        background: 'linear-gradient(135deg, var(--btn-secondary), var(--btn-secondary-hover))',
                        boxShadow: '0 2px 8px rgba(118, 75, 162, 0.3)',
                        color: 'white',
                        textDecoration: 'none',
                        borderRadius: '8px',
                        fontSize: '15px',
                        fontWeight: '600',
                        transition: 'all 0.3s ease',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                      }}
                    >
                      <span>{link.platform}</span>
                      <span>→</span>
                    </a>
                  ))
                ) : (
                  <span style={{
                    color: 'var(--text-secondary)',
                    fontSize: '14px',
                    fontStyle: 'italic'
                  }}>
                    No store links available
                  </span>
                )}
              </div>
            </div>

            {/* Key Request Section - Indie Dev Controls */}
            {currentUser && game.userId && 
             String(currentUser._id) === String(game.userId._id || game.userId) && (
              <div style={{ marginBottom: '24px' }}>
                <h4 style={{
                  color: 'var(--text-primary)',
                  fontSize: '16px',
                  fontWeight: '600',
                  marginBottom: '12px'
                }}>
                  Content Creator Keys
                </h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  <button
                    onClick={() => setShowConfigModal(true)}
                    style={{
                      padding: '14px 24px',
                      background: 'linear-gradient(135deg, #00d9ff, #0099cc)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '15px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease'
                    }}
                  >
                    {keyRequestFormActive ? '⚙️ Manage Key Request Settings' : '🔑 Enable Key Requests'}
                  </button>
                  {keyRequestFormActive && (
                    <button
                      onClick={() => setShowRequestDashboard(true)}
                      style={{
                        padding: '14px 24px',
                        background: 'linear-gradient(135deg, #667eea, #764ba2)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        fontSize: '15px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease'
                      }}
                    >
                      📋 View Key Requests
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Key Request Section - Content Creator Button */}
            {(() => {
              const condition1 = currentUser;
              const condition2 = game.userId;
              const condition3 = String(currentUser?._id) !== String(game.userId?._id || game.userId);
              const condition4 = keyRequestFormActive;
              const condition5 = currentUser?.accountType === 'content_creator';
              
              console.log('[DEBUG] Button visibility check:', {
                hasCurrentUser: condition1,
                hasGameUserId: condition2,
                isDifferentUser: condition3,
                formActive: condition4,
                isContentCreator: condition5,
                currentUserId: currentUser?._id,
                gameOwnerId: game.userId?._id || game.userId,
                accountType: currentUser?.accountType
              });
              
              return condition1 && condition2 && condition3 && condition4 && condition5 ? (
                <div style={{ marginBottom: '24px' }}>
                  <button
                    onClick={() => setShowKeyRequestModal(true)}
                    style={{
                      width: '100%',
                      padding: '16px 24px',
                      background: 'linear-gradient(135deg, #f093fb, #f5576c)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '16px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      boxShadow: '0 4px 12px rgba(245, 87, 108, 0.3)'
                    }}
                  >
                    🎮 Request Key for Content Creation
                  </button>
                </div>
              ) : null;
            })()}

            {/* Hashtags */}
            {game.hashtags && game.hashtags.length > 0 && (
              <div>
                <h4 style={{
                  color: 'var(--text-primary)',
                  fontSize: '16px',
                  fontWeight: '600',
                  marginBottom: '12px'
                }}>
                  Tags
                </h4>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {game.hashtags.map((tag, index) => (
                    <Link
                      key={index}
                      to={`/indie-games?hashtag=${tag}`}
                      style={{
                        padding: '6px 16px',
                        background: 'linear-gradient(135deg, var(--btn-secondary), var(--btn-secondary-hover))',
                        color: 'white',
                        borderRadius: '20px',
                        fontSize: '13px',
                        fontWeight: '500',
                        textDecoration: 'none',
                        transition: 'all 0.3s ease'
                      }}
                    >
                      #{tag}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Key Request Modals */}
      {showKeyRequestModal && (
        <KeyRequestModal
          isOpen={showKeyRequestModal}
          onClose={() => {
            setShowKeyRequestModal(false);
            fetchGameDetails();
          }}
          gameId={id}
          gameName={game.name}
          currentUser={currentUser}
        />
      )}

      {showConfigModal && (
        <KeyRequestFormConfig
          gameId={id}
          onClose={() => {
            setShowConfigModal(false);
            fetchGameDetails();
          }}
        />
      )}

      {showRequestDashboard && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.9)',
          zIndex: 1000,
          overflow: 'auto',
          padding: '20px'
        }}>
          <div style={{
            maxWidth: '1200px',
            margin: '0 auto',
            background: '#1a1a2e',
            borderRadius: '10px',
            padding: '20px'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px'
            }}>
              <h2 style={{ color: '#00d9ff', margin: 0 }}>Key Requests for {game.name}</h2>
              <button
                onClick={() => setShowRequestDashboard(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#fff',
                  fontSize: '32px',
                  cursor: 'pointer'
                }}
              >
                &times;
              </button>
            </div>
            <KeyRequestDashboard
              currentUser={currentUser}
              gameId={id}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default IndieGameDetail;
